<script setup lang="ts">
import { ref, computed, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import api from '@/services/api'
import { useUserSettingsStore } from '@/stores/userSettingsStore'
import type { Group } from '@/types/Group'
import type { PaginatedResponse } from '@/types/PaginatedResponse'

const router = useRouter()
const settingsStore = useUserSettingsStore()

const groups = ref<Group[]>([])
const isLoadingGroups = ref(false)
const groupsError = ref<string | null>(null)
const searchQuery = ref('')
const selectedGroupId = ref<string | null>(settingsStore.settings?.groupId ?? null)
const isSaving = ref(false)
const saveError = ref<string | null>(null)
const saved = ref(false)

const currentGroup = computed(() =>
    groups.value.find(g => g.id === settingsStore.settings?.groupId) ?? null
)

const filteredGroups = computed(() => {
    if (!searchQuery.value) return groups.value
    const q = searchQuery.value.toLowerCase()
    return groups.value.filter(g =>
        g.name?.toLowerCase().includes(q) ||
        g.description?.toLowerCase().includes(q)
    )
})

const fetchGroups = async () => {
    isLoadingGroups.value = true
    groupsError.value = null
    try {
        const response = await api.get<PaginatedResponse<Group>[]>('/groups')
        const paged = Array.isArray(response.data) ? response.data[0] : response.data
        groups.value = paged?.entities ?? []
    } catch (err) {
        groupsError.value = 'Failed to load groups. Please try again.'
        console.error(err)
    } finally {
        isLoadingGroups.value = false
    }
}

const save = async () => {
    if (!selectedGroupId.value) return
    isSaving.value = true
    saveError.value = null
    saved.value = false
    try {
        await settingsStore.saveSettings({ groupId: selectedGroupId.value })
        saved.value = true
    } catch (err) {
        saveError.value = 'Failed to save. Please try again.'
        console.error(err)
    } finally {
        isSaving.value = false
    }
}

onMounted(fetchGroups)
</script>

<template>
    <div class="flex flex-col items-center px-4 py-12">
        <div class="w-full max-w-xl">

            <!-- Header -->
            <div class="mb-8">
                <button @click="router.back()" class="flex items-center gap-1 text-gray-400 hover:text-white text-sm mb-4 transition-colors">
                    <svg class="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7" />
                    </svg>
                    Back
                </button>
                <h1 class="text-2xl font-bold text-white mb-4">Settings</h1>

                <!-- Current group card -->
                <label class="block text-sm font-medium text-gray-400 mb-2">Current group</label>
                <div v-if="currentGroup" class="flex items-center gap-3 px-4 py-3 rounded-lg border border-gray-600 bg-gray-800/50">
                    <img
                        v-if="currentGroup.images?.length"
                        :src="currentGroup.images[0].imageUri"
                        class="h-9 w-9 rounded-full object-cover flex-shrink-0"
                        alt=""
                    />
                    <div
                        v-else
                        class="h-9 w-9 rounded-full bg-gray-600 flex-shrink-0 flex items-center justify-center text-sm font-bold text-gray-300"
                    >
                        {{ currentGroup.name?.charAt(0) }}
                    </div>
                    <div class="flex-1 min-w-0">
                        <p class="font-medium text-white truncate">{{ currentGroup.name }}</p>
                        <p class="text-sm text-gray-500 truncate">
                            {{ currentGroup.memberCount ?? 0 }} members
                            <span v-if="currentGroup.description"> · {{ currentGroup.description }}</span>
                        </p>
                    </div>
                </div>
                <div v-else-if="!isLoadingGroups" class="px-4 py-3 rounded-lg border border-gray-700 bg-gray-800/50 text-gray-500 text-sm">
                    None set
                </div>
            </div>

            <!-- Search -->
            <label class="block text-sm font-medium text-gray-300 mb-2">Change default group</label>
            <input
                v-model="searchQuery"
                type="text"
                placeholder="Search groups..."
                class="w-full px-4 py-2 mb-4 rounded-lg border border-gray-700 bg-gray-800 text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
            />

            <!-- Loading -->
            <div v-if="isLoadingGroups" class="flex justify-center py-16">
                <svg class="animate-spin h-8 w-8 text-blue-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4" />
                    <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
                </svg>
            </div>

            <!-- Error -->
            <div v-else-if="groupsError" class="text-center py-10">
                <p class="text-red-400 mb-4">{{ groupsError }}</p>
                <button
                    @click="fetchGroups"
                    class="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors duration-150"
                >
                    Retry
                </button>
            </div>

            <!-- Group list -->
            <div v-else class="space-y-2 max-h-[26rem] overflow-y-auto pr-1">
                <button
                    v-for="group in filteredGroups"
                    :key="group.id"
                    @click="selectedGroupId = group.id; saved = false"
                    class="w-full text-left px-4 py-3 rounded-lg border transition-colors duration-150 focus:outline-none"
                    :class="selectedGroupId === group.id
                        ? 'bg-blue-600 border-blue-500 text-white'
                        : 'bg-gray-800 border-gray-700 text-gray-300 hover:border-blue-500 hover:bg-gray-700'"
                >
                    <div class="flex items-center gap-3">
                        <img
                            v-if="group.images?.length"
                            :src="group.images[0].imageUri"
                            class="h-9 w-9 rounded-full object-cover flex-shrink-0"
                            alt=""
                        />
                        <div
                            v-else
                            class="h-9 w-9 rounded-full bg-gray-600 flex-shrink-0 flex items-center justify-center text-sm font-bold text-gray-300"
                        >
                            {{ group.name?.charAt(0) }}
                        </div>

                        <div class="flex-1 min-w-0">
                            <p class="font-medium truncate">{{ group.name }}</p>
                            <p class="text-sm truncate" :class="selectedGroupId === group.id ? 'text-blue-200' : 'text-gray-500'">
                                {{ group.memberCount ?? 0 }} members
                                <span v-if="group.description"> · {{ group.description }}</span>
                            </p>
                        </div>

                        <svg
                            v-if="selectedGroupId === group.id"
                            class="h-5 w-5 text-white flex-shrink-0"
                            fill="none" stroke="currentColor" viewBox="0 0 24 24"
                        >
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7" />
                        </svg>
                    </div>
                </button>

                <p v-if="filteredGroups.length === 0" class="text-center text-gray-500 py-10">
                    No groups match your search.
                </p>
            </div>

            <!-- Save -->
            <div class="mt-6 flex items-center gap-3">
                <button
                    @click="save"
                    :disabled="!selectedGroupId || isSaving"
                    class="flex-1 px-6 py-3 bg-blue-600 text-white font-medium rounded-lg shadow-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-150"
                >
                    {{ isSaving ? 'Saving...' : 'Save' }}
                </button>
                <p v-if="saved" class="text-green-400 text-sm flex items-center gap-1">
                    <svg class="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7" />
                    </svg>
                    Saved
                </p>
                <p v-if="saveError" class="text-red-400 text-sm">{{ saveError }}</p>
            </div>

        </div>
    </div>
</template>
